import React, { useState, useEffect } from 'react';
import { templateData } from '../../data/data_templates';
import { promptData } from '../../data/data_prompts';
import './UnifiedTemplateForm.css';

function UnifiedTemplateForm({ template, onSubmit, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    role: '',
    description: '',
    active: true
  });
  
  // State for objectives and tasks management
  const [objectives, setObjectives] = useState([]);
  const [focuses, setFocuses] = useState([]);
  const [conclusions, setConclusions] = useState([]);
  const [objectivesWithTasks, setObjectivesWithTasks] = useState({});
  const [collapsedObjectives, setCollapsedObjectives] = useState(new Set());
  
  // Form states
  const [showObjectiveForm, setShowObjectiveForm] = useState(false);
  const [newObjective, setNewObjective] = useState({ name: '', description: '', prompt_id: null, llm_model: null });
  const [editingObjective, setEditingObjective] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', description: '' });
  const [editingTask, setEditingTask] = useState(null);
  const [showFocusForm, setShowFocusForm] = useState(false);
  const [newFocus, setNewFocus] = useState({ name: '', description: '' });
  const [editingFocus, setEditingFocus] = useState(null);
  const [showConclusionForm, setShowConclusionForm] = useState(false);
  const [newConclusion, setNewConclusion] = useState({ name: '', description: '' });
  const [editingConclusion, setEditingConclusion] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formType, setFormType] = useState('objective');
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [loadingSections, setLoadingSections] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState(null);
  
  // Prompt scripts state
  const [promptScripts, setPromptScripts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  
  // LLM models state
  const [llmModels, setLlmModels] = useState([]);
  const [loadingLlmModels, setLoadingLlmModels] = useState(false);

  const totalSteps = 4; // Basic info, Objectives & Tasks, Focuses, Conclusions

  // Load prompt scripts and LLM models
  useEffect(() => {
    loadPromptScripts();
    loadLlmModels();
  }, []);

  const loadPromptScripts = async () => {
    try {
      setLoadingPrompts(true);
      const response = await promptData.getPromptScripts();
      if (response && response.success) {
        const scripts = response.promptScripts || [];
        setPromptScripts(scripts);
      }
    } catch (err) {
      console.error('Error loading prompt scripts:', err);
    } finally {
      setLoadingPrompts(false);
    }
  };

  const loadLlmModels = async () => {
    try {
      setLoadingLlmModels(true);
      const response = await promptData.getLLMModels('active');
      if (response && response.success) {
        const models = response.llmModels || [];
        setLlmModels(models);
      }
    } catch (err) {
      console.error('Error loading LLM models:', err);
    } finally {
      setLoadingLlmModels(false);
    }
  };

  useEffect(() => {
    if (template) {
      // Editing existing template
      setFormData({
        name: template.name || '',
        type: (template.type || '').toLowerCase(), // Normalize to lowercase to match select options
        role: template.role || '',
        description: template.description || '',
        active: template.active !== undefined ? template.active : true
      });
      
      // Load existing sections
      loadTemplateSections();
    } else {
      // Creating new template
      setFormData({
        name: '',
        type: '',
        role: '',
        description: '',
        active: true
      });
      setObjectives([]);
      setFocuses([]);
      setConclusions([]);
    }
  }, [template]);

  const loadTemplateSections = async () => {
    if (!template) return;
    
    try {
      setLoadingSections(true);
      setError(null); // Clear any previous errors
      
      // Load objectives
      const objectivesResponse = await templateData.getTemplateObjectives(template.template_id);
      if (objectivesResponse && objectivesResponse.success) {
        const objectivesList = objectivesResponse.objectives || [];
        setObjectives(objectivesList);
        
        // Set all objectives as collapsed by default
        const allObjectiveIds = objectivesList.map(obj => obj.objectives_id);
        setCollapsedObjectives(new Set(allObjectiveIds));
        
        // Load tasks for each objective
        const objectivesWithTasksData = {};
        for (const objective of objectivesList) {
          try {
            const tasksResponse = await templateData.getObjectiveTasks(objective.objectives_id);
            if (tasksResponse && tasksResponse.success) {
              const tasks = tasksResponse.tasks || [];
              objectivesWithTasksData[objective.objectives_id] = tasks;
            } else {
              objectivesWithTasksData[objective.objectives_id] = [];
            }
          } catch (taskErr) {
            console.warn('Failed to load tasks for objective:', objective.objectives_id, taskErr);
            objectivesWithTasksData[objective.objectives_id] = [];
          }
        }
        setObjectivesWithTasks(objectivesWithTasksData);
      } else {
        setObjectives([]);
        setObjectivesWithTasks({});
      }
      
      // Load focuses
      try {
        const focusesResponse = await templateData.getTemplateFocuses(template.template_id);
        if (focusesResponse && focusesResponse.success) {
          setFocuses(focusesResponse.focuses || []);
        } else {
          setFocuses([]);
        }
      } catch (focusErr) {
        console.warn('Failed to load focuses:', focusErr);
        setFocuses([]);
      }
      
      // Load conclusions
      try {
        const conclusionsResponse = await templateData.getTemplateConclusions(template.template_id);
        if (conclusionsResponse && conclusionsResponse.success) {
          setConclusions(conclusionsResponse.conclusions || []);
        } else {
          setConclusions([]);
        }
      } catch (conclusionErr) {
        console.warn('Failed to load conclusions:', conclusionErr);
        setConclusions([]);
      }
    } catch (err) {
      console.error('Error loading template sections:', err);
      // Only set error for unexpected failures, not for missing data
      if (err.message && !err.message.includes('404') && !err.message.includes('not found')) {
        setError('Error loading template sections');
      }
    } finally {
      setLoadingSections(false);
    }
  };

  // Collapse/expand functionality
  const toggleObjectiveCollapse = (objectiveId) => {
    setCollapsedObjectives(prev => {
      const newSet = new Set(prev);
      if (newSet.has(objectiveId)) {
        newSet.delete(objectiveId);
      } else {
        newSet.add(objectiveId);
      }
      return newSet;
    });
  };

  const isObjectiveCollapsed = (objectiveId) => {
    return collapsedObjectives.has(objectiveId);
  };

  // Objective management functions
  const handleCreateObjective = async () => {
    if (!newObjective.name.trim()) {
      setError('Objective name is required');
      return;
    }

    if (!template) {
      setError('Template must be saved before adding objectives');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await templateData.createObjective(
        template.template_id,
        newObjective.name,
        newObjective.description,
        objectives.length + 1,
        newObjective.prompt_id, // prompt_id
        newObjective.llm_model // llm_model
      );

      if (response && response.success) {
        const newObjectiveData = response.objective;
        setObjectives([...objectives, newObjectiveData]);
        setObjectivesWithTasks(prev => ({
          ...prev,
          [newObjectiveData.objectives_id]: []
        }));
        setNewObjective({ name: '', description: '', prompt_id: null, llm_model: null });
        setShowObjectiveForm(false);
      } else {
        setError('Failed to create objective');
      }
    } catch (err) {
      console.error('Error creating objective:', err);
      setError('Error creating objective');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteObjective = async (objectiveId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await templateData.deleteObjective(objectiveId);
      if (response && response.success) {
        setObjectives(objectives.filter(obj => obj.objectives_id !== objectiveId));
        const newObjectivesWithTasks = { ...objectivesWithTasks };
        delete newObjectivesWithTasks[objectiveId];
        setObjectivesWithTasks(newObjectivesWithTasks);
        
        if (selectedObjective === objectiveId) {
          setSelectedObjective(null);
        }
      } else {
        setError('Failed to delete objective');
      }
    } catch (err) {
      console.error('Error deleting objective:', err);
      setError('Error deleting objective');
    } finally {
      setLoading(false);
    }
  };

  const handleEditObjective = (objective) => {
    setEditingObjective(objective);
    setNewObjective({
      name: objective.name || '',
      description: objective.description || '',
      prompt_id: objective.prompt_id || null,
      llm_model: objective.llm_model || null
    });
    setShowObjectiveForm(true);
  };

  const handleUpdateObjective = async () => {
    if (!newObjective.name.trim()) {
      setError('Objective name is required');
      return;
    }

    if (!editingObjective) {
      setError('No objective selected for editing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await templateData.updateObjective(
        editingObjective.objectives_id,
        newObjective.name,
        newObjective.description,
        editingObjective.ordinal,
        newObjective.prompt_id,
        editingObjective.active,
        newObjective.llm_model
      );

      if (response && response.success) {
        setObjectives(objectives.map(obj => 
          obj.objectives_id === editingObjective.objectives_id 
            ? { ...obj, name: newObjective.name, description: newObjective.description, prompt_id: newObjective.prompt_id, llm_model: newObjective.llm_model }
            : obj
        ));
        setNewObjective({ name: '', description: '', prompt_id: null, llm_model: null });
        setEditingObjective(null);
        setShowObjectiveForm(false);
        
        // Reload objectives to ensure we have the latest data from the database
        await loadTemplateSections();
      } else {
        setError('Failed to update objective');
      }
    } catch (err) {
      console.error('Error updating objective:', err);
      setError('Error updating objective');
    } finally {
      setLoading(false);
    }
  };

  // Task management functions
  const handleCreateTask = async () => {
    if (!newTask.name.trim()) {
      setError('Task name is required');
      return;
    }

    if (!selectedObjective) {
      setError('Please select an objective first');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await templateData.createTask(
        selectedObjective,
        newTask.name,
        newTask.description,
        newTask.description_short,
        (objectivesWithTasks[selectedObjective] || []).length + 1
      );

      if (response && response.success) {
        const newTaskData = response.task;
        setObjectivesWithTasks(prev => ({
          ...prev,
          [selectedObjective]: [...(prev[selectedObjective] || []), newTaskData]
        }));
        setNewTask({ name: '', description: '' });
        setShowTaskForm(false);
        setSelectedObjective(null);
      } else {
        setError('Failed to create task');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Error creating task');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await templateData.deleteTask(taskId);
      if (response && response.success) {
        // Find which objective this task belongs to
        const objectiveId = Object.keys(objectivesWithTasks).find(objId => 
          objectivesWithTasks[objId].some(task => {
            const taskTaskId = task.tasks_id || task.id || task.task_id;
            return taskTaskId === taskId;
          })
        );
        
        if (objectiveId) {
          setObjectivesWithTasks(prev => ({
            ...prev,
            [objectiveId]: prev[objectiveId].filter(task => {
              const taskTaskId = task.tasks_id || task.id || task.task_id;
              return taskTaskId !== taskId;
            })
          }));
        }
      } else {
        setError('Failed to delete task');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Error deleting task');
    } finally {
      setLoading(false);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setNewTask({
      name: task.name || '',
      description: task.description || ''
    });
    setShowTaskForm(true);
  };

  const handleUpdateTask = async () => {
    if (!newTask.name.trim()) {
      setError('Task name is required');
      return;
    }

    if (!editingTask) {
      setError('No task selected for editing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Try to get the task ID from different possible property names
      const taskId = editingTask.tasks_id || editingTask.id || editingTask.task_id;
      
      if (!taskId) {
        console.error('No task ID found in editingTask:', editingTask);
        setError('Task ID not found');
        setLoading(false);
        return;
      }

      const response = await templateData.updateTask(
        taskId,
        newTask.name,
        newTask.description
      );

      if (response && response.success) {
        // Find which objective this task belongs to
        const objectiveId = Object.keys(objectivesWithTasks).find(objId => 
          objectivesWithTasks[objId].some(task => {
            const taskTaskId = task.tasks_id || task.id || task.task_id;
            const editingTaskId = editingTask.tasks_id || editingTask.id || editingTask.task_id;
            return taskTaskId === editingTaskId;
          })
        );
        
        if (objectiveId) {
          setObjectivesWithTasks(prev => ({
            ...prev,
            [objectiveId]: prev[objectiveId].map(task => {
              const taskTaskId = task.tasks_id || task.id || task.task_id;
              const editingTaskId = editingTask.tasks_id || editingTask.id || editingTask.task_id;
              return taskTaskId === editingTaskId
                ? { ...task, name: newTask.name, description: newTask.description }
                : task;
            })
          }));
        }
        setNewTask({ name: '', description: '' });
        setEditingTask(null);
        setShowTaskForm(false);
      } else {
        setError('Failed to update task');
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Error updating task');
    } finally {
      setLoading(false);
    }
  };

  // Focus management functions
  const handleCreateFocus = async () => {
    if (!newFocus.name.trim()) {
      setError('Focus name is required');
      return;
    }

    if (!template) {
      setError('Template must be saved before adding focus areas');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await templateData.createFocus(
        template.template_id,
        newFocus.name,
        newFocus.description,
        focuses.length + 1
      );

      if (response && response.success) {
        setFocuses([...focuses, response.focus]);
        setNewFocus({ name: '', description: '' });
        setShowFocusForm(false);
      } else {
        setError('Failed to create focus area');
      }
    } catch (err) {
      console.error('Error creating focus area:', err);
      setError('Error creating focus area');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFocus = async (focusId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await templateData.deleteFocus(focusId);
      if (response && response.success) {
        setFocuses(focuses.filter(f => f.id !== focusId));
      } else {
        setError('Failed to delete focus area');
      }
    } catch (err) {
      console.error('Error deleting focus area:', err);
      setError('Error deleting focus area');
    } finally {
      setLoading(false);
    }
  };

  const handleEditFocus = (focus) => {
    setEditingFocus(focus);
    setNewFocus({ name: focus.name, description: focus.description });
    setShowFocusForm(true);
  };

  const handleUpdateFocus = async () => {
    if (!newFocus.name.trim()) {
      setError('Focus area name is required');
      return;
    }

    if (!editingFocus) {
      setError('No focus area selected for editing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await templateData.updateFocus(
        editingFocus.id,
        newFocus.name,
        newFocus.description,
        editingFocus.ordinal || 1,
        editingFocus.active !== undefined ? editingFocus.active : true
      );

      if (response && response.success) {
        setFocuses(focuses.map(f => 
          f.id === editingFocus.id 
            ? { ...f, name: newFocus.name, description: newFocus.description }
            : f
        ));
        setNewFocus({ name: '', description: '' });
        setShowFocusForm(false);
        setEditingFocus(null);
      } else {
        setError('Failed to update focus area');
      }
    } catch (err) {
      console.error('Error updating focus area:', err);
      setError('Error updating focus area');
    } finally {
      setLoading(false);
    }
  };

  // Conclusion management functions
  const handleCreateConclusion = async () => {
    if (!newConclusion.name.trim()) {
      setError('Conclusion name is required');
      return;
    }

    if (!template) {
      setError('Template must be saved before adding conclusions');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await templateData.createConclusion(
        template.template_id,
        newConclusion.name,
        newConclusion.description,
        conclusions.length + 1
      );

      if (response && response.success) {
        setConclusions([...conclusions, response.conclusion]);
        setNewConclusion({ name: '', description: '' });
        setShowConclusionForm(false);
      } else {
        setError('Failed to create conclusion');
      }
    } catch (err) {
      console.error('Error creating conclusion:', err);
      setError('Error creating conclusion');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConclusion = async (conclusionId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await templateData.deleteConclusion(conclusionId);
      if (response && response.success) {
        setConclusions(conclusions.filter(c => c.id !== conclusionId));
      } else {
        setError('Failed to delete conclusion');
      }
    } catch (err) {
      console.error('Error deleting conclusion:', err);
      setError('Error deleting conclusion');
    } finally {
      setLoading(false);
    }
  };

  const handleEditConclusion = (conclusion) => {
    setEditingConclusion(conclusion);
    setNewConclusion({ name: conclusion.name, description: conclusion.description });
    setShowConclusionForm(true);
  };

  const handleUpdateConclusion = async () => {
    if (!newConclusion.name.trim()) {
      setError('Conclusion name is required');
      return;
    }

    if (!editingConclusion) {
      setError('No conclusion selected for editing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await templateData.updateConclusion(
        editingConclusion.id,
        newConclusion.name,
        newConclusion.description,
        editingConclusion.ordinal || 1,
        editingConclusion.active !== undefined ? editingConclusion.active : true
      );

      if (response && response.success) {
        setConclusions(conclusions.map(c => 
          c.id === editingConclusion.id 
            ? { ...c, name: newConclusion.name, description: newConclusion.description }
            : c
        ));
        setNewConclusion({ name: '', description: '' });
        setShowConclusionForm(false);
        setEditingConclusion(null);
      } else {
        setError('Failed to update conclusion');
      }
    } catch (err) {
      console.error('Error updating conclusion:', err);
      setError('Error updating conclusion');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    if (!formData.type.trim()) {
      setError('Type is required');
      return;
    }

    if (!formData.role.trim()) {
      setError('Role is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Submit the form data with isNextStep flag as false (final submission)
      await onSubmit(formData, false);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Error saving template');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const nextStep = async () => {
    if (currentStep < totalSteps) {
      // Save the current form data before proceeding
      try {
        setLoading(true);
        setError(null);
        
        // Submit the form data with isNextStep flag
        await onSubmit(formData, true);
        
        // If save is successful, proceed to next step
        setCurrentStep(currentStep + 1);
      } catch (err) {
        console.error('Error saving template:', err);
        setError('Error saving template. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (stepNumber) => {
    // Only allow navigation to steps that are accessible
    // For editing existing templates, all steps should be accessible
    // For new templates, only allow navigation to step 1 initially
    if (template || stepNumber === 1) {
      setCurrentStep(stepNumber);
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="step-indicator">
        {[1, 2, 3, 4].map((step) => {
          const isAccessible = template || step === 1;
          return (
            <div 
              key={step} 
              className={`step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''} ${isAccessible ? 'clickable' : 'disabled'}`}
              onClick={() => isAccessible && goToStep(step)}
              style={{ cursor: isAccessible ? 'pointer' : 'default' }}
            >
              <div className="step-number">{step}</div>
              <div className="step-label">
                {step === 1 && 'Basic Info'}
                {step === 2 && 'Objectives & Tasks'}
                {step === 3 && 'Focuses'}
                {step === 4 && 'Conclusions'}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderBasicInfo = () => {
    return (
      <div className="form-section basic-info">
        <h3>Basic Information</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Template Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter template name"
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="type">Type *</label>
            <select
              id="type"
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
            >
              <option value="">Select type</option>
              <option value="consulting">Consulting</option>
              <option value="design">Design</option>
              <option value="engineering">Engineering</option>
              <option value="hr">HR</option>
              <option value="humor">Humor</option>
              <option value="marketing">Marketing</option>
              <option value="misc">Misc</option>
              <option value="outreach">Outreach</option>
              <option value="product">Product</option>
              <option value="sales">Sales</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="role">Meeting Focus *</label>
            <input
              type="text"
              id="role"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              placeholder="Enter meeting focus"
              required
            />
          </div>
        </div>

        <div className="form-row description-row">
          <div className="form-group full-width">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter template description"
              rows="4"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="active"
                checked={formData.active}
                onChange={handleInputChange}
              />
              <span className="checkmark"></span>
              Active
            </label>
          </div>
        </div>
      </div>
    );
  };

  const renderObjectivesAndTasks = () => {
    return (
      <div className="form-section objectives-tasks-section">
        <h3>Objectives & Tasks</h3>
        <p className="section-description">
          Define the main objectives for this template and their associated tasks. These will guide the conversation or meeting.
        </p>
        
        {!template ? (
          <div className="template-not-saved">
            <i className="ti ti-info-circle"></i>
            <p>Please save the template first to add objectives and tasks</p>
          </div>
        ) : loadingSections ? (
          <div className="loading-sections">
            <i className="ti ti-loader ti-spin"></i>
            <p>Loading objectives and tasks...</p>
          </div>
        ) : (
          <div className="objectives-tasks-container">
            {/* Objectives List with Tasks */}
            <div className="objectives-section">
              <div className="section-header">
                <h4>Objectives & Tasks</h4>
                <button 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowObjectiveForm(true);
                  }}
                  className="add-button"
                  disabled={loading}
                >
                  <i className="ti ti-plus"></i>
                  Add Objective
                </button>
              </div>

              {/* New/Edit Objective Form */}
              {showObjectiveForm && (
                <div className="new-item-form">
                  <div className="form-group">
                    <label>Objective Name *</label>
                    <input
                      type="text"
                      value={newObjective.name}
                      onChange={(e) => setNewObjective(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter objective name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={newObjective.description}
                      onChange={(e) => setNewObjective(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Enter objective description"
                      rows="2"
                    />
                  </div>
                  <div className="form-group">
                    <label className="optional-label">
                      Prompt Script (Optional)&nbsp;&nbsp;
                      <span className="help-icon">
                        ?
                      </span>
                    </label>
                    <select
                      value={newObjective.prompt_id || ''}
                      onChange={(e) => setNewObjective(prev => ({ 
                        ...prev, 
                        prompt_id: e.target.value ? parseInt(e.target.value) : null 
                      }))}
                      disabled={loadingPrompts}
                    >
                      <option value="">Select a prompt script (optional)</option>
                                            {promptScripts.length === 0 ? (
                        <option value="" disabled>
                          {loadingPrompts ? 'Loading...' : 'No prompt scripts available'}
                        </option>
                      ) : (
                        promptScripts.map((script) => (
                          <option key={script.id} value={script.id}>
                            {script.name}
                          </option>
                        ))
                      )}
                    </select>
                    {loadingPrompts && (
                      <div className="loading-indicator">
                        <i className="ti ti-loader ti-spin"></i>
                        Loading prompt scripts...
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="optional-label">
                      LLM Model (Optional)&nbsp;&nbsp;
                      <span className="help-icon">
                        ?
                      </span>
                    </label>
                    <select
                      value={newObjective.llm_model || ''}
                      onChange={(e) => setNewObjective(prev => ({ 
                        ...prev, 
                        llm_model: e.target.value ? parseInt(e.target.value) : null 
                      }))}
                      disabled={loadingLlmModels}
                    >
                      <option value="">Select an LLM model (optional)</option>
                      {llmModels.length === 0 ? (
                        <option value="" disabled>
                          {loadingLlmModels ? 'Loading...' : 'No LLM models available'}
                        </option>
                      ) : (
                        llmModels.map((model) => (
                          <option key={model.id} value={model.connection_id}>
                            {model.name}
                          </option>
                        ))
                      )}
                    </select>
                    {loadingLlmModels && (
                      <div className="loading-indicator">
                        <i className="ti ti-loader ti-spin"></i>
                        Loading LLM models...
                      </div>
                    )}
                  </div>
                  <div className="form-actions">
                    <button 
                      onClick={editingObjective ? handleUpdateObjective : handleCreateObjective}
                      className="save-button"
                      disabled={loading}
                    >
                      {loading ? <i className="ti ti-loader ti-spin"></i> : <i className="ti ti-check"></i>}
                      {editingObjective ? 'Update Objective' : 'Save Objective'}
                    </button>
                    <button 
                      onClick={() => {
                        setShowObjectiveForm(false);
                        setNewObjective({ name: '', description: '', prompt_id: null, llm_model: null });
                        setEditingObjective(null);
                      }}
                      className="cancel-button"
                      disabled={loading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Objectives List with Tasks */}
              <div className="objectives-list">
                {objectives.length === 0 ? (
                  <div className="empty-sections">
                    <i className="ti ti-target"></i>
                    <p>No objectives defined yet</p>
                    <p className="empty-hint">Click "Add Objective" to get started</p>
                  </div>
                ) : (
                  objectives.map((objective, index) => (
                    <div key={objective.objectives_id} className={`objective-item ${isObjectiveCollapsed(objective.objectives_id) ? 'collapsed' : ''}`}>
                      <div className="objective-header">
                        <div className="objective-info">
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleObjectiveCollapse(objective.objectives_id);
                            }}
                            className="collapse-button"
                            title={isObjectiveCollapsed(objective.objectives_id) ? "Expand" : "Collapse"}
                          >
                            <i className={`ti ti-chevron-${isObjectiveCollapsed(objective.objectives_id) ? 'right' : 'down'}`}></i>
                          </button>
                          <span className="section-ordinal">{index + 1}</span>
                          <h5>{objective.name} ({(objectivesWithTasks[objective.objectives_id] || []).length})</h5>
                        </div>
                        <div className="objective-actions">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEditObjective(objective);
                            }}
                            className="edit-button"
                            disabled={loading}
                            title="Edit Objective"
                          >
                            <i className="ti ti-edit"></i>
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteObjective(objective.objectives_id);
                            }}
                            className="delete-button"
                            disabled={loading}
                            title="Delete Objective"
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        </div>
                      </div>
                      
                      {!isObjectiveCollapsed(objective.objectives_id) && (
                        <div key={`objective-content-${objective.objectives_id}`}>
                          {objective.description && (
                            <p className="objective-description">{objective.description}</p>
                          )}
                          
                          {/* Tasks for this objective */}
                          <div key={`objective-tasks-${objective.objectives_id}`} className="objective-tasks">
                            <div className="tasks-header">
                              <h6>Tasks</h6>
                              <button 
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSelectedObjective(objective.objectives_id);
                                  setShowTaskForm(true);
                                }}
                                className="add-task-button"
                                disabled={loading}
                              >
                                <i className="ti ti-plus"></i>
                                Add Task
                              </button>
                            </div>
                            
                            {/* New/Edit Task Form for this objective */}
                            {showTaskForm && (selectedObjective === objective.objectives_id || editingTask) && (
                                                              <div key={`new-task-form-${objective.objectives_id}`} className="new-task-form">
                                  <div className="form-group">
                                    <label>Task Name *</label>
                                    <input
                                      type="text"
                                      value={newTask.name}
                                      onChange={(e) => setNewTask(prev => ({ ...prev, name: e.target.value }))}
                                      placeholder="Enter task name"
                                    />
                                  </div>
                                  <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                      value={newTask.description}
                                      onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                                      placeholder="Enter task description"
                                      rows="2"
                                    />
                                  </div>
                                                                  <div className="form-actions">
                                    <button 
                                      onClick={editingTask ? handleUpdateTask : handleCreateTask}
                                      className="save-button"
                                      disabled={loading}
                                    >
                                      {loading ? <i className="ti ti-loader ti-spin"></i> : <i className="ti ti-check"></i>}
                                      {editingTask ? 'Update Task' : 'Save Task'}
                                    </button>
                                    <button 
                                      onClick={() => {
                                        setShowTaskForm(false);
                                        setNewTask({ name: '', description: '' });
                                        setSelectedObjective(null);
                                        setEditingTask(null);
                                      }}
                                      className="cancel-button"
                                      disabled={loading}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                              </div>
                            )}
                            
                            {/* Tasks List for this objective */}
                            <div className="tasks-list">
                              {(!objectivesWithTasks[objective.objectives_id] || objectivesWithTasks[objective.objectives_id].length === 0) ? (
                                <div key={`empty-tasks-${objective.objectives_id}`} className="empty-tasks">
                                  <i className="ti ti-checklist"></i>
                                  <p>No tasks defined for this objective</p>
                                </div>
                              ) : (
                                objectivesWithTasks[objective.objectives_id].map((task, taskIndex) => (
                                  <div key={task.tasks_id || task.id || task.task_id} className="task-item">
                                    <div className="task-header">
                                      <div className="task-info">
                                        <span className="task-ordinal">{taskIndex + 1}</span>
                                        <div className="task-title-container">
                                          <h6>{task.name}</h6>
                                        </div>
                                      </div>
                                      <div className="task-actions">
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleEditTask(task);
                                          }}
                                          className="edit-button"
                                          disabled={loading}
                                          title="Edit Task"
                                        >
                                          <i className="ti ti-edit"></i>
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDeleteTask(task.tasks_id || task.id || task.task_id);
                                          }}
                                          className="delete-button"
                                          disabled={loading}
                                          title="Delete Task"
                                        >
                                          <i className="ti ti-trash"></i>
                                        </button>
                                      </div>
                                    </div>
                                    {task.description && (
                                      <div className="task-description-container">
                                        <p className="task-description">{task.description}</p>
                                      </div>
                                    )}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderFocuses = () => {
    return (
      <div className="form-section">
        <h3>Focus Areas</h3>
        <p className="section-description">
          Define key focus areas for this template. These help guide the conversation in specific directions.
        </p>
        
        {!template ? (
          <div className="template-not-saved">
            <i className="ti ti-info-circle"></i>
            <p>Please save the template first to add focus areas</p>
          </div>
        ) : loadingSections ? (
          <div className="loading-sections">
            <i className="ti ti-loader ti-spin"></i>
            <p>Loading focus areas...</p>
          </div>
        ) : (
          <div className="focuses-container">
            <div className="section-header">
              <h4>Focus Areas</h4>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowFocusForm(true);
                }}
                className="add-button"
                disabled={loading}
              >
                <i className="ti ti-plus"></i>
                Add Focus Area
              </button>
            </div>

            {/* New Focus Form */}
            {showFocusForm && (
              <div className="new-item-form">
                <div className="form-group">
                  <label>{editingFocus ? 'Edit Focus Area Name *' : 'Focus Area Name *'}</label>
                  <input
                    type="text"
                    value={newFocus.name}
                    onChange={(e) => setNewFocus(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter focus area name"
                  />
                </div>
                <div className="form-group">
                  <label>{editingFocus ? 'Edit Description' : 'Description'}</label>
                  <textarea
                    value={newFocus.description}
                    onChange={(e) => setNewFocus(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={editingFocus ? "Enter updated focus area description" : "Enter focus area description"}
                    rows="3"
                  />
                </div>
                <div className="form-actions">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      editingFocus ? handleUpdateFocus() : handleCreateFocus();
                    }}
                    className="save-button"
                    disabled={loading}
                  >
                    {loading ? <i className="ti ti-loader ti-spin"></i> : <i className="ti ti-check"></i>}
                    {editingFocus ? 'Update Focus Area' : 'Save Focus Area'}
                  </button>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowFocusForm(false);
                      setNewFocus({ name: '', description: '' });
                      setEditingFocus(null);
                    }}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Focus Areas List */}
            <div className="focuses-list">
              {focuses.length === 0 ? (
                <div className="empty-sections">
                  <i className="ti ti-focus"></i>
                  <p>No focus areas defined yet</p>
                  <p className="empty-hint">Click "Add Focus Area" to get started</p>
                </div>
              ) : (
                focuses.map((focus, index) => (
                  <div key={focus.id} className="focus-item">
                    <div className="focus-header">
                      <div className="focus-info">
                        <span className="section-ordinal">{index + 1}</span>
                        <h5>{focus.name}</h5>
                      </div>
                      <div className="focus-actions">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditFocus(focus);
                          }}
                          className="edit-button"
                          disabled={loading}
                          title="Edit Focus Area"
                        >
                          <i className="ti ti-edit"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteFocus(focus.id);
                          }}
                          className="delete-button"
                          disabled={loading}
                          title="Delete Focus Area"
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      </div>
                    </div>
                    {focus.description && (
                      <p className="focus-description">{focus.description}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderConclusions = () => {
    return (
      <div className="form-section">
        <h3>Conclusions</h3>
        <p className="section-description">
          Define conclusion points for this template. These help wrap up the conversation or meeting effectively.
        </p>
        
        {!template ? (
          <div className="template-not-saved">
            <i className="ti ti-info-circle"></i>
            <p>Please save the template first to add conclusions</p>
          </div>
        ) : loadingSections ? (
          <div className="loading-sections">
            <i className="ti ti-loader ti-spin"></i>
            <p>Loading conclusions...</p>
          </div>
        ) : (
          <div className="conclusions-container">
            <div className="section-header">
              <h4>Conclusions</h4>
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowConclusionForm(true);
                }}
                className="add-button"
                disabled={loading}
              >
                <i className="ti ti-plus"></i>
                Add Conclusion
              </button>
            </div>

            {/* New Conclusion Form */}
            {showConclusionForm && (
              <div className="new-item-form">
                <div className="form-group">
                  <label>{editingConclusion ? 'Edit Conclusion Name *' : 'Conclusion Name *'}</label>
                  <input
                    type="text"
                    value={newConclusion.name}
                    onChange={(e) => setNewConclusion(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={editingConclusion ? "Enter updated conclusion name" : "Enter conclusion name"}
                  />
                </div>
                <div className="form-group">
                  <label>{editingConclusion ? 'Edit Description' : 'Description'}</label>
                  <textarea
                    value={newConclusion.description}
                    onChange={(e) => setNewConclusion(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={editingConclusion ? "Enter updated conclusion description" : "Enter conclusion description"}
                    rows="3"
                  />
                </div>
                <div className="form-actions">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      editingConclusion ? handleUpdateConclusion() : handleCreateConclusion();
                    }}
                    className="save-button"
                    disabled={loading}
                  >
                    {loading ? <i className="ti ti-loader ti-spin"></i> : <i className="ti ti-check"></i>}
                    {editingConclusion ? 'Update Conclusion' : 'Save Conclusion'}
                  </button>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowConclusionForm(false);
                      setNewConclusion({ name: '', description: '' });
                      setEditingConclusion(null);
                    }}
                    className="cancel-button"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Conclusions List */}
            <div className="conclusions-list">
              {conclusions.length === 0 ? (
                <div className="empty-sections">
                  <i className="ti ti-check"></i>
                  <p>No conclusions defined yet</p>
                  <p className="empty-hint">Click "Add Conclusion" to get started</p>
                </div>
              ) : (
                conclusions.map((conclusion, index) => (
                  <div key={conclusion.id} className="conclusion-item">
                    <div className="conclusion-header">
                      <div className="conclusion-info">
                        <span className="section-ordinal">{index + 1}</span>
                        <h5>{conclusion.name}</h5>
                      </div>
                      <div className="conclusion-actions">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditConclusion(conclusion);
                          }}
                          className="edit-button"
                          disabled={loading}
                          title="Edit Conclusion"
                        >
                          <i className="ti ti-edit"></i>
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteConclusion(conclusion.id);
                          }}
                          className="delete-button"
                          disabled={loading}
                          title="Delete Conclusion"
                        >
                          <i className="ti ti-trash"></i>
                        </button>
                      </div>
                    </div>
                    {conclusion.description && (
                      <p className="conclusion-description">{conclusion.description}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderObjectivesAndTasks();
      case 3:
        return renderFocuses();
      case 4:
        return renderConclusions();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <div className="unified-template-form-overlay" data-testid="template-editor">
      <div className="unified-template-form-modal">
        <div className="form-header">
          <h2>{template ? 'Edit Template' : 'Create New Template'}</h2>
          <button onClick={handleCancel} className="close-button">
            <i className="ti ti-x"></i>
          </button>
        </div>

        {renderStepIndicator()}

        <form onSubmit={handleSubmit} className="unified-template-form" data-testid="template-form">
          {error && (
            <div className="error-message">
              <i className="ti ti-alert-circle"></i>
              {error}
            </div>
          )}

          {renderCurrentStep()}

          <div className="form-actions">
            <div className="form-actions-left">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="prev-button"
                  disabled={loading}
                >
                  <i className="ti ti-chevron-left"></i>
                  Previous
                </button>
              )}
            </div>
            
            <div className="form-actions-right">
              <button
                type="button"
                onClick={handleCancel}
                className="cancel-button"
                disabled={loading}
              >
                Cancel
              </button>
              
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="next-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="ti ti-loader ti-spin"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <i className="ti ti-chevron-right"></i>
                    </>
                  )}
                </button>
              ) : (
                <button
                  type="submit"
                  className="submit-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="ti ti-loader ti-spin"></i>
                      Saving...
                    </>
                  ) : (
                    template ? 'Save & Close' : 'Create Template'
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UnifiedTemplateForm; 